import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { RestfulApiService } from '../restful-api.service';

/**
 * User terms list from API
 */
export interface UserTermList {
  CHAMPION_DISCLOSURE: boolean;
  CODE_OF_CONDUCT: boolean;
  AI_DISCLOSURE: boolean;
  TS_AND_PRIVACY: boolean;
}

/**
 * User information response from get-user-info API
 */
export interface UserInfoResponse {
  response: {
    userUuid: string;
    firstName: string;
    middleName: string | null;
    lastName: string;
    completedRegistration: boolean;
    role: string;
    region: string | null;
    email: string;
    country: string;
    countryCode: string;
    stateProvince: string;
    city: string;
    zipcode: string;
    landmarkAddress: string;
    linkedinProfile: string;
    personalWebSite: string | null;
    lastAskedQuestionDate: string | null;
    userType: string;
    status: string;
    emailChange: boolean;
    socialUser: boolean;
    userVetted: boolean;
    bio: string;
    jobTitle: string;
    userTermList: UserTermList;
    accreditedInvestorOptionsUuids: any;
    fullAddress: string;
    createdOrganisation: any;
    waitingRoom: boolean;
    globalCurrency: any;
    entityDto: any;
    partOfAnyGroups: boolean;
    invitedToJoinGroupCounter: number;
    requestsToJoinGroupsCounter: number;
    languageCode: string;
    champion: any;
    badge: any;
    userCharityRoles?: UserCharityRole[];
  };
}

/**
 * Champion DTO for account update payload
 */
export interface ChampionDto {
  championDescription: string;
  championDisclosureRequirements: boolean;
  championTypes: string[];
  dealsInvestedRange: string;
  experience: string;
  grantOrDueDiligence: boolean;
  grantMakingOrDiligenceOrganisation: string | null;
  interestedCategories: string[];
  interestedCauses: string[];
  interestedCountries: string[];
  mediaUuid: string | null;
  website: string | null;
}

/**
 * Account update payload for the account-update API
 */
export interface AccountUpdatePayload {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email: string;
  fullAddress: string | null;
  landmarkAddress?: string;
  country: string;
  countryCode?: string;
  stateProvince: string;
  city: string;
  codeOfConduct: boolean;
  termsOfService: boolean;
  uuid: string;
  zipcode?: string;
  bio: string;
  title?: string;
  linkedinProfile: string | null;
  championDto?: ChampionDto | null;
}

/**
 * Account update response from the API
 */
export interface AccountUpdateResponse {
  response: any;
  message?: string;
}

/**
 * Upload user file response from the API
 */
export interface UploadUserFileResponse {
  response: {
    url: string;
    fileType: string;
  };
  message?: string;
}

/**
 * Upload champion file response from the API
 */
export interface UploadChampionFileResponse {
  response: {
    uuid: string;
    url: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
  };
  message?: string;
}

/**
 * Upload champion file parameters
 */
export interface UploadChampionFileParams {
  entityUuid: string;
  fileType: string;
  file: File;
  secondUuid?: string;
}

/**
 * Sort configuration for users list
 */
export interface UsersSortBy {
  field: string;
  order: 'ASC' | 'DESC';
  checked: boolean;
}

/**
 * Payload for getting users list
 */
export interface GetUsersPayload {
  pending: boolean;
  page: number;
  size: number;
  userTypes: string[];
  roles: string[];
  vetted: boolean | null;
  accreditedInvestorsDisclosure: boolean;
  searchText: string;
  sortBy: UsersSortBy;
  sortByInvitedUser: any | null;
  waitingRoom: boolean;
  completedRegistration: boolean;
  archive: boolean;
  organisationUuid: string | null;
  userStatus: string[];
  charitiesUuid?: string[];
  /**
   * True when the users list is filtered by charity and the synthetic
   * "All Charities" option is selected — the backend then returns users from
   * every charity and `charitiesUuid` carries no filter. False in every other
   * case (including individual charity selections).
   */
  allCharities: boolean;
}

/**
 * Accredited investor information
 */
export interface AccreditedInvestorDto {
  accreditedInvestorsDisclosure: boolean;
  accreditedInvestorOptions: any[];
}

/**
 * Image information
 */
export interface ImageDto {
  uuid: string | null;
  url: string | null;
  name: string;
}

/**
 * Entity information (organization)
 */
export interface EntityDto {
  uuid: string;
  name: string;
}

/**
 * User object in the users list
 */
export interface UserListItem {
  uuid: string;
  roleUuid: string;
  profileImgUrl: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  roleValue: string;
  vetted: boolean;
  waitingRoom: boolean;
  champion: boolean;
  userType: string;
  userTypeValue: string;
  userStatus: string;
  completedProfile: boolean;
  archived: boolean;
  entityDto: EntityDto | null;
  partnerUuid: string | null;
  highResolutionImage: ImageDto;
  fdiAdminImage: ImageDto;
  linkedinProfile: string | null;
  tellMeAboutYourself: string | null;
  organizationName: string | null;
  historyOverview: string | null;
  organizationType: string | null;
  languageEditor: boolean;
  rankingScore: number;
  accreditedInvestorDto: AccreditedInvestorDto;
  userCharityRoles?: UserCharityRole[];
}

/**
 * Charity role assigned to a user, as returned by the get-users API
 */
export interface UserCharityRole {
  subRole: string;
  charityUuid: string;
  charityName: string | null;
}

/**
 * Response from get users list API
 */
export interface GetUsersResponse {
  response: {
    pageNumber: number;
    totalObjects: number;
    from: number;
    to: number;
    objects: UserListItem[];
  };
}

/**
 * User profile response from get-user-profile API
 */
export interface UserProfileResponse {
  response: {
    privileges: string[];
    accessToken: string | null;
    userUuid: string | null;
    firstName: string;
    middleName: string | null;
    lastName: string;
    completedRegistration: boolean;
    role: string;
    region: string | null;
    email: string;
    country: string | null;
    countryCode: string | null;
    stateProvince: string | null;
    city: string | null;
    zipcode: string | null;
    landmarkAddress: string | null;
    linkedinProfile: string | null;
    personalWebSite: string | null;
    lastAskedQuestionDate: string | null;
    userType: string;
    status: string;
    profileImageUrl: string | null;
    emailChange: boolean;
    socialUser: boolean;
    userVetted: boolean;
    bio: string | null;
    jobTitle: string | null;
    userTermList: UserTermList;
    accreditedInvestorOptionsUuids: any;
    fullAddress: string | null;
    createdOrganisation: boolean;
    waitingRoom: boolean;
    globalCurrency: string | null;
    entityDto: EntityDto | null;
    partOfAnyGroups: boolean;
    invitedToJoinGroupCounter: number;
    requestsToJoinGroupsCounter: number;
    languageCode: string | null;
    champion: any;
    badge: any;
  };
}

/**
 * Payload for changing user permissions
 */
export interface CharityRole {
  subRole: string;
  charityUuid: string;
}

export interface ChangePermissionPayload {
  userUuid: string;
  userType: string;
  entityUuid: string | null;
  newRole: string;
  champion: boolean;
  userVetted: boolean;
  waitingRoom: boolean;
  languageEditor: boolean;
  rankingScore: number;
  charityRoles?: CharityRole[];
}

/**
 * Payload for updating user profile
 */
export interface UpdateUserProfilePayload {
  firstName: string;
  middleName: string | null;
  lastName: string;
  email: string;
  fullAddress: string | null;
  codeOfConduct: boolean;
  termsOfService: boolean;
  bio: string | null;
  title: string | null;
  linkedinProfile: string | null;
  championDto?: ChampionDto | null;
}

@Injectable({
  providedIn: 'root'
})
export class GsUserService {
  // Shared replay cache so app-init and dashboard share one /users/get-user-info request.
  // Cleared on error (so next call retries), on user mutations, and on logout.
  private userInfo$: Observable<UserInfoResponse> | null = null;

  constructor(private restfulApiService: RestfulApiService) {}

  /**
   * Get user information from the API.
   * Cached with `shareReplay(1)` — concurrent subscribers share one HTTP call,
   * and subsequent calls within the same session reuse the cached response.
   * Call `resetUserInfoCache()` after mutations or on logout.
   */
  getUserInfo(): Observable<UserInfoResponse> {
    if (!this.userInfo$) {
      this.userInfo$ = (
        this.restfulApiService.get('/v1/users/get-user-info') as Observable<UserInfoResponse>
      ).pipe(
        catchError((error) => {
          this.userInfo$ = null;
          return throwError(() => error);
        }),
        shareReplay(1)
      );
    }
    return this.userInfo$;
  }

  /**
   * Invalidate the cached `getUserInfo` response. The next subscriber will
   * trigger a fresh HTTP request.
   */
  resetUserInfoCache(): void {
    this.userInfo$ = null;
  }

  /**
   * Update user account information
   * @param payload Account update payload
   * @returns Observable of account update response
   */
  updateAccountInfo(payload: AccountUpdatePayload): Observable<AccountUpdateResponse> {
    return (this.restfulApiService.post('/v1/users/account-update', payload) as Observable<AccountUpdateResponse>).pipe(
      tap(() => this.resetUserInfoCache())
    );
  }

  /**
   * Upload user file (e.g., profile image)
   * @param file The file blob to upload
   * @param fileType The type of file (e.g., 'PROFILE_IMAGE')
   * @returns Observable of upload response
   */
  uploadUserFile(file: Blob, fileType: string = 'PROFILE_IMAGE'): Observable<UploadUserFileResponse> {
    const formData = new FormData();
    formData.append('fileType', fileType);
    formData.append('file', file, 'profile-image.jpg');

    return this.restfulApiService.post('/v1/users/upload-user-file', formData) as Observable<UploadUserFileResponse>;
  }

  /**
   * Get users list from the admin endpoint
   * @param payload Get users payload with filters and pagination
   * @returns Observable of users list response
   */
  getUsers(payload: GetUsersPayload): Observable<GetUsersResponse> {
    return this.restfulApiService.post('/v1/admin/get-users', payload) as Observable<GetUsersResponse>;
  }

  /**
   * Get user profile from the admin endpoint
   * @param userId The UUID of the user to get profile for
   * @returns Observable of user profile response
   */
  getUserProfile(userId: string): Observable<UserProfileResponse> {
    return this.restfulApiService.get(`/v1/admin/get-user-profile/${userId}`) as Observable<UserProfileResponse>;
  }


  /**
   * Archive a user (move to archive)
   * @param uuid User UUID to archive
   * @returns Observable of archive response
   */
  archiveUser(uuid: string): Observable<any> {
    return this.restfulApiService.put(`/v1/admin/archive-user/${uuid}`, {});
  }

  /**
   * Unarchive (restore) a user from archive
   * @param uuid User UUID to restore
   * @returns Observable of unarchive response
   */
  unarchiveUser(uuid: string): Observable<any> {
    return this.restfulApiService.put(`/v1/admin/unarchive-user/${uuid}`, {});
  }

  /**
   * Delete a user permanently
   * @param uuid User UUID to delete
   * @returns Observable of delete response
   */
  deleteUser(uuid: string): Observable<any> {
    return this.restfulApiService.delete(`/v1/admin/delete-user/${uuid}`);
  }

  /**
   * Resend invitation email to a pending user
   * @param tokenUuid Token UUID of the pending invitation
   * @returns Observable of resend response
   */
  resendInvitation(tokenUuid: string): Observable<any> {
    return this.restfulApiService.post(`/v1/users/resend-invitation?tokenUuid=${tokenUuid}`, {});
  }

  /**
   * Delete a pending invitation
   * @param email Email address of the invitation to delete
   * @returns Observable of delete invitation response
   */
  deleteInvitation(email: string): Observable<any> {
    return this.restfulApiService.post('/v1/users/delete-invitation', { emails: [email] });
  }

  /**
   * Upload champion file (image/video/document)
   * @param params Upload champion file parameters including entityUuid, fileType, and file
   * @returns Observable of upload champion file response
   */
  uploadChampionFile(params: UploadChampionFileParams): Observable<UploadChampionFileResponse> {
    const formData = new FormData();
    formData.append('entityUuid', params.entityUuid);
    formData.append('fileType', params.fileType);
    formData.append('secondUuid', params.secondUuid || params.entityUuid);
    formData.append('file', params.file);

    return this.restfulApiService.post('/v1/general/upload/file', formData) as Observable<UploadChampionFileResponse>;
  }

  /**
   * Reset user password by sending reset link to their email
   * @param email User email address to send password reset link
   * @returns Observable of reset password response
   */
  resetPassword(email: string): Observable<any> {
    return this.restfulApiService.post('/v1/admin/reset-password-link', { email });
  }

  /**
   * Change user permissions and details
   * @param payload Change permission payload with user details
   * @returns Observable of change permission response
   */
  changeUserPermissions(payload: ChangePermissionPayload): Observable<any> {
    return this.restfulApiService.post('/v1/admin/change-permission', payload);
  }

  /**
   * Update user profile information
   * @param userId User UUID to update
   * @param payload User profile update payload
   * @returns Observable of update response
   */
  updateUserProfile(userId: string, payload: UpdateUserProfilePayload): Observable<any> {
    return this.restfulApiService.post(`/v1/admin/user-profile-update/${userId}`, payload).pipe(
      tap(() => this.resetUserInfoCache())
    );
  }

  /**
   * Upload a high-resolution photo file
   * @param file The image file to upload
   * @returns Observable of upload response
   */
  uploadHiresFile(entityUuid: string, file: File): Observable<UploadChampionFileResponse> {
    const formData = new FormData();
    formData.append('entityUuid', entityUuid);
    formData.append('fileType', 'CHAMPION_HIGH_QUALITY_IMAGE');
    formData.append('secondUuid', '');
    formData.append('file', file);
    return this.restfulApiService.post('/v1/general/upload/file', formData) as Observable<UploadChampionFileResponse>;
  }
}
